/**
 * Walking on a sphere in real time (Phase 59, the action clock). Pure.
 *
 * A unit is a direction from its planet's centre (`surfaceDir`) plus a **heading** — a
 * unit vector in the ground plane. A step rotates both about the same axis (parallel
 * transport), so the heading stays true across the whole sphere with no pole where a
 * fixed tangent frame would flip. Rocks, trees and structures block feet; ground steeper
 * than ACTION_MAX_SLOPE stops the step. Uses only exact maths plus dmath, so a walk is
 * the same on every engine.
 */

import type { FloraState, GameConfig, GameState, PlanetState, PlayerState, RockState, StructureState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecDot, vecCross, vecNormalize, vecLength, surfaceTangentFrame } from '../math';
import { dsin, dcos, dacos } from './dmath';
import { getSurfaceRadius } from './surface';
import { personSurfaceRadius } from './fineTerrain';
import { seatRadius } from './propSeat';
import { fallenRest, restPoint, type LyingRest } from './lyingRest';
import { buildCellIndex, queryCellIndexCount, cellQueryIds, cellsPerEdge, cellsOfItems, fileCellIndex, kthOfSort, allFinite, cellOf, type CellIndex } from './cells';
import { floraSplice, type FloraSplice } from './floraSplice';
import { surfacePropScale } from './propScale';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { structureJob, drives } from './body';
import { clockPolicyFor } from '../match/clockPolicy';
import { TOWER_BODY_FRACTION_OF_EYE } from '../constants/body';

/** An information tower's solid body as a fraction of its eye height — the table's (X18); kept as a name. */
export const TOWER_BODY_FRACTION = TOWER_BODY_FRACTION_OF_EYE;
import {
  ACTION_UNIT_RADIUS, ACTION_MAX_SLOPE, ACTION_STEP_OVER,
  ROCK_MODE_SCALES, ROCK_MODEL_SIZE, TREE_TRUNK_SIZE, TREE_DRAW_SCALE, STRUCTURE_MODEL_SIZE, NORMAL_TREE_PERSONS, NORMAL_ROCK_PERSONS, NORMAL_ROCK_SIZE, NORMAL_ROCK_PROFILE, NORMAL_ROCK_HULL, LYING_ROCKS, ROCK_SINK_FRACTION, TREE_SINK_FRACTION,
} from '../constants';
import { buildingsByFunction, isPlanetBuilding, structureHitbox, structureRuinHeight, ruinHitbox, hitboxExtent } from './buildingScale';
import { TREE_WOOD, type WoodPiece, type TreeWoodVariant } from '../constants/treeWood';
import { treeVariant, treeYaw, treeFallYaw, treeFrame, rockVariant, rockYaw } from './treeDraws';
import { drawnHeightScale, drawnPieces, drawnWoodVariant } from './treeShape';
import { treeForm, treeFormShape, formsKey, debrisPieces, debrisExtent, debrisRest } from './debris';
import { simWork } from './workCounters';

/**
 * A cheap, exact pre-test for "is the arc between two unit directions shorter than `angle`?" —
 * `dacos(dot) < angle` can only hold if `dot ≥ 1 − angle²/2`, because cos x ≥ 1 − x²/2. The margin
 * (1e-12 in the dot, far above dacos's last-bit error at any obstacle's angle) keeps the shortcut on
 * the safe side, so callers that skip on `false` get exactly the answers the full test gives (E5: a
 * battle dressed with a few hundred trunks made the per-obstacle arc-cosine the sim's hot spot).
 */
export function mayBeWithin(dot: number, angle: number): boolean {
  return dot >= 1 - (angle * angle) / 2 - 1e-12;
}

/** What an obstacle is: a rock, a tree or a structure, by id (63.3 step 3). */
export interface ObstacleRef { kind: 'rock' | 'flora' | 'structure' | 'ruin' | 'debris'; id: number }

/** Something on the ground a unit cannot walk through — and, below its height, bullets cannot pass (cover). */
/**
 * 63.16 (i) (the owner: "Sure 3, 4, 6" on lane F's known-wrong list): a wall's box on the ground — the exact rectangle a disc of
 * a building's outline stands for (core/hits.ts outlineDiscs): its centre `c` (a unit direction), its axes along the ground
 * and its half extents (world). The disc is the broad phase (every disc of a box lies inside it); the walk and the airborne
 * then meet the rectangle itself, so a wall's end is its end and a corner is a corner (the perimeter discs cut a corner by
 * 0.41 of their radius: a body walked into a building's corner stood with its centre on the corner itself).
 */
export interface WalkBox { c: Vec3; ax: Vec3; az: Vec3; hx: number; hz: number }

export interface WalkObstacle {
  dir: Vec3; radius: number; height: number;
  /** 63.16 (i): the box this disc stands for (a building's wall, crate or container); the walk meets the rectangle, not the disc. */
  box?: WalkBox;
  /** 67.13: the cover's half-width at each tenth of `height`, × `radius` (absent = a cylinder); `sink` = the share of `height` drawn under the ground. Shots and sight only. */
  profile?: readonly number[]; sink?: number;
  /** 63.3 step 3: what it is — so a shot that stops at it can say what it hit (core/hits.ts). Walking ignores it. */
  ref?: ObstacleRef;
  /**
   * E5.2: a tree's wood as drawn (constants/treeWood.ts, measured): frustums between end points given as offsets (world
   * units and directions) from the obstacle's foot on the ground — a leaning trunk, a curved palm, the limbs; a felled
   * tree's stump and its trunk lying along its fall. Shots and sight test these instead of the cylinder (core/hits.ts);
   * walking still reads `radius` and `height` (the trunk's foot, or the stump).
   */
  pieces?: readonly ObstaclePiece[];
  /**
   * 71.2: a rock's, a tree's or a piece of debris' seat on the fine ground (core/propSeat.ts seatGround): its own direction
   * object (the entity's, so every reader seats it alike, the drawing too) and its footprint. Absent (a structure, a ruin):
   * it stands on the mesh plus its build's lift, as before.
   */
  seat?: { dir: Vec3; half: number };
  /** 63.3 step 4: a felled tree — the pieces from this index on are its trunk lying along the ground (before it, the stump); walking meets them as a row of discs (core/hits.ts footprintObstacles). */
  lyingFrom?: number;
  /** 70.4: its lying pieces rest on the fine ground along their length (core/lyingRest.ts), so their discs stand at their height over the ground under each (absent: over the foot's plane, as before). */
  rested?: true;
}

/** E5.2: one frustum of a tree's wood — its end centres `a`, `b` as offsets from the foot, their radii `ra`, `rb` (world units). */
export interface ObstaclePiece { a: Vec3; b: Vec3; ra: number; rb: number }

/**
 * E5.2: a drawn tree's wood pieces in the world — the model's pieces scaled by `k` (world units per model unit), sunk as
 * the drawing seats the tree (TREE_SINK_FRACTION of its `height`), and turned into the tree's frame (`up`, `yaw`).
 */
export function treePieces(pieces: readonly WoodPiece[], up: Vec3, yaw: number, k: number, height: number): ObstaclePiece[] {
  const toWorld = treeFrame(up, yaw), sink = TREE_SINK_FRACTION * height;
  return pieces.map(p => ({ a: toWorld(p[0] * k, (p[1] - sink) * k, p[2] * k), b: toWorld(p[4] * k, (p[5] - sink) * k, p[6] * k), ra: p[3] * k, rb: p[7] * k }));
}

/**
 * 70.4: a lying tree's wood in the world from its foot — the model's pieces scaled by `k` and tipped onto its rest on the
 * ground (core/lyingRest.ts), not sunk as a standing tree is drawn (it lies on the ground, sunk by its material alone),
 * and turned to the fall (`up`, `yaw`).
 */
export function restedPieces(pieces: readonly WoodPiece[], up: Vec3, yaw: number, k: number, rest: LyingRest): ObstaclePiece[] {
  const toWorld = treeFrame(up, yaw);
  return pieces.map(p => ({ a: restPoint(toWorld, rest, p[0] * k, p[1] * k, p[2] * k), b: restPoint(toWorld, rest, p[4] * k, p[5] * k, p[6] * k), ra: p[3] * k, rb: p[7] * k }));
}

/**
 * 63.17 (the owner: "also giant tree collision circles are way too big"): a tree's walk radius in model units — the widest
 * its standing wood reaches from the axis between the drawn ground (the sink) and `band` above it, a person's height on the
 * tree's own ruler: the trunk's foot, a leaning trunk or a bottle tree's belly where a body meets them. TREE_TRUNK_SIZE.half
 * is the widest drawn trunk at any height (a giant oak's circle 1.5× its foot); it still seats the drawing.
 */
export function woodFootRadius(v: TreeWoodVariant, band: number): number {
  const lo = TREE_SINK_FRACTION * v.height, hi = lo + band;
  let r = 0;
  const at = (p: WoodPiece, t: number) => {
    const x = p[0] + (p[4] - p[0]) * t, z = p[2] + (p[6] - p[2]) * t;
    r = Math.max(r, Math.sqrt(x * x + z * z) + p[3] + (p[7] - p[3]) * t);
  };
  for (const p of v.whole) {
    const dy = p[5] - p[1];
    if (dy === 0) { if (p[1] >= lo && p[1] <= hi) { at(p, 0); at(p, 1); } continue; }
    const ta = (lo - p[1]) / dy, tb = (hi - p[1]) / dy, t0 = Math.max(0, Math.min(ta, tb)), t1 = Math.min(1, Math.max(ta, tb));
    // the reach along a frustum is convex in t (a line's distance from the axis plus a linear radius): its ends bound it
    if (t0 <= t1) { at(p, t0); at(p, t1); }
  }
  return r;
}

const giantTrunks = new Map<TreeWoodVariant, Map<number, WoodPiece[]>>();
/**
 * 63.17: a giant tree's trunk for shots, in model units — the chain of upright wood from the foot (each piece starting on the
 * one below and within ~30° of vertical) up to `top`, each piece stood on the axis: a giant is drawn turned its own way
 * (rendering/renderer.ts), so a lean or a limb would stand in the wrong place, but the trunk's width at each height — the
 * bottle tree's belly — holds at any turn. The crown is not here (concealment, not cover: the normal trees' rule).
 */
export function giantTrunk(v: TreeWoodVariant, top: number): WoodPiece[] {
  const memo = giantTrunks.get(v) ?? new Map<number, WoodPiece[]>();
  giantTrunks.set(v, memo);
  const hit = memo.get(top);
  if (hit) return hit;
  const out: WoodPiece[] = [];
  const upright = (p: WoodPiece) => { const dx = p[4] - p[0], dy = p[5] - p[1], dz = p[6] - p[2]; return dy > 0 && dy >= 0.85 * Math.sqrt(dx * dx + dy * dy + dz * dz); };
  let prev = v.whole.find(p => Math.abs(p[1]) < 1e-3 && p[0] * p[0] + p[2] * p[2] <= p[3] * p[3] && upright(p));   // the foot: on the ground, the axis inside it
  while (prev && prev[1] < top) {
    const [, y0, , r0, , y1, , r1] = prev;
    const t = y1 > top ? (top - y0) / (y1 - y0) : 1;
    out.push([0, y0, 0, r0, 0, y0 + (y1 - y0) * t, 0, r0 + (r1 - r0) * t]);
    const below = prev;
    const reach = Math.max(below[3], below[7]);
    prev = v.whole.find(p => {
      const dx = p[0] - below[4], dz = p[2] - below[6];
      return p !== below && upright(p) && p[5] > below[5] && p[1] <= below[5] + 1e-3 && dx * dx + dz * dz <= reach * reach;
    });
  }
  memo.set(top, out);
  return out;
}

/**
 * A structure that is person-scale, and its half width (u): an information tower always (I1), an emplacement on the
 * action clock (64.11; lane E's EMPLACEMENT_MODEL_SIZE). Null for the planet-scale kinds. One size for its walk
 * obstacle and its blast reach (Phase 63's rule: a prop's reach follows its own scale).
 */
export function personScaleStructureHalfWidth(s: Pick<StructureState, 'type'>, config: GameState['config'] | undefined): number | null {
  if (!config) return null;
  // X18: the table says which structures are person-scale bodies, and by which ruler
  const cover = structureJob(s).cover;
  if (!cover || (cover.when === 'action' && clockPolicyFor(config).kind !== 'continuous')) return null;   // 64.11's rule is the continuous clock's
  const ps = personSetOf({ config });
  return cover.ruler === 'unit' ? ps.unitRadius * cover.half : cover.half * (PERSON_BODY_HEIGHT * ps.k) / 1.8;
}

/**
 * The obstacles on one planet — rocks, tree trunks, structures (a ruin at its rubble's height) — sized exactly as
 * the renderer draws them (constants.ts ROCK_MODEL_SIZE & co., × surfacePropScale). Props
 * lower than ACTION_STEP_OVER are left out: units step over pebbles, and they hide nobody.
 */
export function walkObstacles(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins'>>, planetIndex: number): WalkObstacle[] {
  return planetObstacles(state, planetIndex).list;
}

/**
 * 62.10 (ARCHITECTURE §5.3): a planet's obstacles, filed by cube-sphere cell. Memoised on everything the list
 * is built from (rocks, flora, structures, the config, the planet itself), so ticks and sight checks stop
 * rebuilding it; the same list object comes back while none of those change. Treat it as read-only.
 */
export interface PlanetObstacles {
  list: WalkObstacle[];
  index: CellIndex;
  /** 63.16 (i): some obstacle carries a box (its rectangle reaches past its disc by up to √2 − 1 of the radius: the queries widen by it). */
  boxed?: true;
  /** The largest radius and height among them (the bounds a query widens by). */
  maxRadius: number;
  maxHeight: number;
}

/**
 * X31: the structures that can be obstacles — all but the vehicles that drive — as an array that stays the same object
 * while those stay the same objects (a moving bike replaces only itself), so the filing below is not rebuilt every tick
 * a bike moves. Memoised per structures array.
 */
const staticMemo = new WeakMap<readonly StructureState[], readonly StructureState[]>();
let lastStatic: readonly StructureState[] = [];
function staticStructures(all: readonly StructureState[] | undefined): readonly StructureState[] | undefined {
  if (!all) return all;
  if (all.length === 0) return NO_STATIC;   // (69.67: one empty list, so a world without structures is one memo key)
  const hit = staticMemo.get(all);
  if (hit) return hit;
  if (!all.some(drives)) { staticMemo.set(all, all); return all; }
  const fresh = all.filter(x => !drives(x));
  const same = fresh.length === lastStatic.length && fresh.every((x, i) => x === lastStatic[i]);
  const out = same ? lastStatic : fresh;
  lastStatic = out;
  staticMemo.set(all, out);
  return out;
}

const NO_STATIC: readonly StructureState[] = [];
/**
 * 69.67 (story-1: the APC loop's let-out spots read the planet without its structures, apcLoop.ts — the moving carriers
 * left out — and each such read and the next ordinary one rebuilt the other's tables, 43–153 full builds a minute): a
 * planet's tables kept apart for the world with its static structures (0) and the world without (1) — its memo, its last
 * filing, and the shapes' and footprints' records over them (core/hits.ts) — so each patches from its own. The same tables.
 */
export function obstacleVariant(state: Pick<GameState, 'structures'>): number {
  const s = staticStructures(state.structures);
  return s && s.length ? 0 : 1;
}
const NO_OBSTACLES: PlanetObstacles = { list: [], index: buildCellIndex(0, () => ({ x: 0, y: 0, z: 1 }), 8), maxRadius: 0, maxHeight: 0 };
type ObstacleMemo = { from: readonly unknown[]; planetIndex: number; value: PlanetObstacles };
/** Per planet, per variant (obstacleVariant). */
let obstacleMemo = new WeakMap<PlanetState, Array<ObstacleMemo | undefined>>();
function memoSet(planet: PlanetState, fk: number, m: ObstacleMemo): void {
  let per = obstacleMemo.get(planet);
  if (!per) { per = []; obstacleMemo.set(planet, per); }
  per[fk & 1] = m;
}

/** 62.56: each planet index's last filing — a refiling of the very same obstacles keeps its cell index. 69.67: by planet index and variant (`planetIndex * 2 + variant`). */
let lastFiled = new Map<number, Filed>();
/**
 * A planet index's last filing. E5.4's tail patch keeps beside it the planet it stood on, each item's cell at the
 * index's cells per edge, and where the flora's obstacles end in the list (rocks, flora, structures, ruins, debris).
 */
interface Filed { radius: number; value: PlanetObstacles; from?: readonly unknown[]; felled?: readonly FelledSlot[]; floraSlot?: Int32Array; median?: number; planet?: PlanetState; cells?: Uint32Array; floraEnd?: number; /** 63.1f's follow-up: each static structure's slot (-1: none) */ structSlot?: Int32Array }

/**
 * E5.4's tail patch: the splice a patched list was made by — the list it was made from, the old items that left (1), and
 * where the joined ones went in (`at`, `count`) — so the shapes and footprints built over it patch alike (core/hits.ts).
 */
export interface ObstacleSplice { prev: PlanetObstacles; gone: Uint8Array; at: number; count: number }
const splices = new WeakMap<PlanetObstacles, ObstacleSplice>();
export function obstacleSplice(po: PlanetObstacles): ObstacleSplice | undefined { return splices.get(po); }

/**
 * 62.56, for the tests: run `fn` with nothing kept — every obstacle built from its entity, every index filed afresh —
 * then put back what was kept (core/hits.ts `fromNothing` wraps this for the shapes).
 */
/**
 * E5.4's tail patch, for the tests: run `fn` with the memo empty (so the table is built afresh, as play would build it on
 * the kept records and the kept entities), then put back the memo and every record as they were — the patched path then
 * runs from the same records it would have.
 */
export function obstaclesBuildScope<T>(fn: () => T): T {
  const saved = { obstacleMemo, lastFiled: new Map(lastFiled) };
  obstacleMemo = new WeakMap();
  try { return fn(); } finally { obstacleMemo = saved.obstacleMemo; lastFiled = saved.lastFiled; }
}

export function obstaclesFromNothing<T>(fn: () => T): T {
  const saved = { entityObstacles, felledObstacles, formedObstacles, obstacleMemo, lastFiled };
  entityObstacles = new WeakMap(); felledObstacles = new WeakMap(); formedObstacles = new WeakMap(); obstacleMemo = new WeakMap(); lastFiled = new Map();
  try { return fn(); } finally { ({ entityObstacles, felledObstacles, formedObstacles, obstacleMemo, lastFiled } = saved); }
}

export function planetObstacles(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'ruins' | 'floraHealth' | 'debris'>>, planetIndex: number): PlanetObstacles {
  const planet = state.planets[planetIndex];
  if (!planet) return NO_OBSTACLES;
  // X31: a bike moving every tick does not refile the planet; the ruins count only where they are bodies (the turn
  // game's claims rebuild them each turn)
  // (partial destruction step 3: the trees' forms and the debris, with the switch on — a null and an absent array off)
  const from = [state.rocks, state.flora, staticStructures(state.structures), state.config, ruinBodies(state), formsKey(state), state.config?.partialDestruction ? state.debris : undefined];
  const fk = planetIndex * 2 + ((from[2] as readonly StructureState[] | undefined)?.length ? 0 : 1);
  const m = obstacleMemo.get(planet)?.[fk & 1];
  if (m && m.planetIndex === planetIndex && m.from.every((x, i) => x === from[i])) return m.value;
  // 67.45: a crater or a felling (or both, a shell's) — every other prop, the config and the forms the same. A tree whose
  // entry changed (felled: a felling keeps each entry's place in the flora) is made again in its own slot, and so is a
  // felled tree's stump over moved ground; the rest are kept as they are. Whether one stands, and its direction, decide the
  // slots and the filing: while those stay, the list is patched in place and its index kept (the cost is the changed
  // trees', not the planet's). The bounds and the cells per edge are read as a full build reads them
  const filed = lastFiled.get(fk);
  const oldFlora = filed?.from?.[1] as readonly FloraState[] | undefined, newFlora = from[1] as readonly FloraState[] | undefined;
  // 63.1f's follow-up: and a static structure replaced in place (a gun pit's turret laid, its seat's reload running down) —
  // the same structures in the same order, each changed one re-made in its own slot (it was a full refile every tick a pit
  // was crewed)
  const oldStatic = filed?.from?.[2] as readonly StructureState[] | undefined, newStatic = from[2] as readonly StructureState[] | undefined;
  if (filed?.from && filed.felled && filed.floraSlot && filed.structSlot && filed.radius === planet.radius && filed.from.every((x, i) => i === 1 || i === 2 || x === from[i])
    && (oldFlora === newFlora || (!!oldFlora && !!newFlora && oldFlora.length === newFlora.length))
    && (oldStatic === newStatic || (!!oldStatic && !!newStatic && oldStatic.length === newStatic.length))) {
    let list = filed.value.list, felled = filed.felled, ok = true, median = filed.median, dirs = true, cells = filed.cells;
    const cow = () => { if (list === filed.value.list) list = list.slice(); };
    if (oldFlora !== newFlora && oldFlora && newFlora) {
      const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
      const ctx: FloraCtx = { state, planetIndex, planet, person, prop: surfacePropScale(planet.radius) / 2, stepOver: state.config ? personSetOf({ config: state.config }).stepOver : ACTION_STEP_OVER };
      for (let j = 0; j < newFlora.length && ok; j++) {
        const f = newFlora[j], was = oldFlora[j];
        if (f === was) continue;
        const slot = filed.floraSlot[j], r = floraObstacle(ctx, f), o = r?.o ?? null;
        if ((slot >= 0) !== (o !== null)) { ok = false; break; }
        if (felled.some(x => x.f === was)) felled = felled.filter(x => x.f !== was);
        if (r?.felled) { r.felled.i = slot; felled = [...felled, r.felled]; }
        if (slot < 0 || o === list[slot]) continue;
        const before = list[slot];
        cow(); list[slot] = o!;
        if (!sameVec(before.dir, o!.dir)) dirs = false;
        // the median radius (the cells per edge) stands while each changed radius stays on its side of it
        if (median !== undefined && !((before.radius < median && o!.radius < median) || (before.radius > median && o!.radius > median))) median = undefined;
      }
    }
    if (ok && oldStatic !== newStatic && oldStatic && newStatic) {
      for (let j = 0; j < newStatic.length && ok; j++) {
        const st = newStatic[j], was = oldStatic[j];
        if (st === was) continue;
        if (st.id !== was.id) { ok = false; break; }
        const slot = filed.structSlot![j], o = st.planetIndex === planetIndex ? structureObstacle(state, planet, st) : null;
        if ((slot >= 0) !== (o !== null)) { ok = false; break; }
        if (slot < 0 || o === list[slot]) continue;
        const before = list[slot];
        cow(); list[slot] = o!;
        if (!sameVec(before.dir, o!.dir)) dirs = false;
        if (median !== undefined && !((before.radius < median && o!.radius < median) || (before.radius > median && o!.radius > median))) median = undefined;
      }
    }
    if (ok) {
      // a felled tree's stump over moved ground (the crater's part): made again in its slot — its size does not read the ground
      for (const x of felled) {
        if (x.i < 0) continue;
        const o = felledObstacle(planet, state.config, x);
        if (o === list[x.i]) continue;
        cow(); list[x.i] = o!;
      }
      let value: PlanetObstacles;
      if (list === filed.value.list) value = { ...filed.value };
      else {
        let maxRadius = 0, maxHeight = 0;
        for (const o of list) { if (o.radius > maxRadius) maxRadius = o.radius; if (o.height > maxHeight) maxHeight = o.height; }
        if (median === undefined) { const radii = Float64Array.from(list, o => o.radius).sort(); median = radii.length ? radii[radii.length >> 1] : undefined; }
        const n = cellsPerEdge(planet.radius, median !== undefined ? median * 2 : planet.radius);
        const keepIndex = dirs && n === filed.value.index.n;
        // (E5.4's tail patch: the items' cells kept with the index they file)
        cells = keepIndex ? cells : cellsOfItems(list.length, i => list[i].dir, n);
        value = { list, index: keepIndex ? filed.value.index : fileCellIndex(cells!, n), maxRadius, maxHeight };
      }
      simWork.tableCraterPatches++;
      memoSet(planet, fk, { from, planetIndex, value });
      lastFiled.set(fk, { ...filed, value, from, felled, median, planet, cells });
      return value;
    }
  }
  // E5.4's tail patch: forest cells joined or left, and nothing else — the list spliced, the rest read as a full build reads it
  if (filed?.from && filed.cells && filed.floraEnd !== undefined && filed.floraSlot && filed.felled && filed.planet === planet && filed.radius === planet.radius
    && oldFlora && newFlora && oldFlora !== newFlora && filed.from.every((x, i) => i === 1 || i === 2 || x === from[i])
    // (63.1f's follow-up beside it: static structures replaced in place — a crewed pit's every tick — re-made in their slots)
    && (oldStatic === newStatic || (!!filed.structSlot && !!oldStatic && !!newStatic && oldStatic.length === newStatic.length))) {
    const sp = floraSplice(oldFlora, newFlora);
    const value = sp ? joinPatch(state, planetIndex, fk, planet, filed, sp, oldFlora, newFlora, from, oldStatic, newStatic) : null;
    if (value) return value;
    if (sp) simWork.tableJoinFallbacks++;
  }
  const felled: FelledSlot[] = [];
  simWork.tableBuilds++;
  const floraSlot = new Int32Array(state.flora?.length ?? 0).fill(-1);
  const structSlot = new Int32Array((from[2] as readonly StructureState[] | undefined)?.length ?? 0).fill(-1);
  const ends = { flora: 0 };
  const list = buildWalkObstacles(state, planetIndex, felled, floraSlot, structSlot, ends);
  let maxRadius = 0, maxHeight = 0;
  for (const o of list) { if (o.radius > maxRadius) maxRadius = o.radius; if (o.height > maxHeight) maxHeight = o.height; }
  // 62.56: the same directions filed again at the same cells per edge (a crater leaves the props alone; 63.4 pd step 1: a
  // felled tree's stump keeps its tree's direction) — the last filing's index, which is its directions and cells per edge alone
  const radii = Float64Array.from(list, o => o.radius).sort();   // numeric ascending, as the comparator sort was (a typed sort: 0.3 ms less at 2,000)
  const typical = radii.length ? radii[radii.length >> 1] * 2 : planet.radius;
  const n = cellsPerEdge(planet.radius, typical);
  const last = lastFiled.get(fk);
  const refiled = last && last.radius === planet.radius && last.value.index.n === n && last.value.list.length === list.length
    && last.value.list.every((o, i) => o === list[i] || sameVec(o.dir, list[i].dir));
  const cells = refiled && last!.cells && last!.cells.length === list.length ? last!.cells : cellsOfItems(list.length, i => list[i].dir, n);
  const index: PlanetObstacles['index'] = refiled ? last!.value.index : fileCellIndex(cells, n);
  const value: PlanetObstacles = { list, index, maxRadius, maxHeight };
  memoSet(planet, fk, { from, planetIndex, value });
  lastFiled.set(fk, { radius: planet.radius, value, from, felled, floraSlot, median: radii.length ? radii[radii.length >> 1] : undefined, planet, cells, floraEnd: ends.flora, structSlot });
  return value;
}

/**
 * E5.4's tail patch (ARCHITECTURE §5.17): the last filing patched for forest cells joining and leaving — the leaving trees'
 * obstacles spliced out, the joining ones' made (as the full build makes them) and put in where the flora's obstacles
 * end; the flora's slots and the felled trees' slots moved with them; the median (the cells per edge) found as the full
 * build's sort finds it; the index filed from the kept cells. Null (a full build) when a radius is not finite.
 */
function joinPatch(
  state: Parameters<typeof planetObstacles>[0], planetIndex: number, fk: number, planet: PlanetState, filed: Filed, sp: FloraSplice,
  oldFlora: readonly FloraState[], newFlora: readonly FloraState[], from: readonly unknown[],
  oldStatic?: readonly StructureState[], newStatic?: readonly StructureState[],
): PlanetObstacles | null {
  const old = filed.value.list, oldSlot = filed.floraSlot!, oldCells = filed.cells!, floraEnd = filed.floraEnd!;
  // the leaving trees' slots
  const gone = new Uint8Array(old.length);
  let goneN = 0;
  const left = new Set<FloraState>();
  for (const j of sp.removed) { left.add(oldFlora[j]); const sl = oldSlot[j]; if (sl >= 0) { gone[sl] = 1; goneN++; } }
  // the joining trees' obstacles, made as the full build makes them (in the new flora's order)
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const stepOver = state.config ? personSetOf({ config: state.config }).stepOver : ACTION_STEP_OVER;
  const ctx: FloraCtx = { state, planetIndex, planet, person, prop: surfacePropScale(planet.radius) / 2, stepOver };
  const add: WalkObstacle[] = [], addFelled: Array<{ x: FelledSlot; k: number }> = [], addAt = new Int32Array(newFlora.length - sp.addStart).fill(-1);
  for (let t = sp.addStart; t < newFlora.length; t++) {
    const r = floraObstacle(ctx, newFlora[t]);
    if (!r) continue;
    if (r.felled) addFelled.push({ x: r.felled, k: r.o ? add.length : -1 });
    if (!r.o) continue;
    addAt[t - sp.addStart] = add.length;
    add.push(r.o);
  }
  // the spliced list and its cells, and where each old item went
  const at = floraEnd - countBelow(gone, floraEnd);
  const count = old.length - goneN + add.length;
  const list = new Array<WalkObstacle>(count), cells = new Uint32Array(count), moved = new Int32Array(old.length).fill(-1);
  let w = 0;
  for (let i = 0; i < floraEnd; i++) { if (gone[i]) continue; moved[i] = w; list[w] = old[i]; cells[w] = oldCells[i]; w++; }
  const n0 = filed.value.index.n;
  for (const o of add) { list[w] = o; cells[w] = cellOf(o.dir.x, o.dir.y, o.dir.z, n0); w++; }
  for (let i = floraEnd; i < old.length; i++) { moved[i] = w; list[w] = old[i]; cells[w] = oldCells[i]; w++; }
  // a static structure replaced in place (the same id at the same place in the list): re-made in its moved slot, as the
  // crater path re-makes it in its own — anything else, a full build
  const structSlot = filed.structSlot ? Int32Array.from(filed.structSlot, sl => (sl >= 0 ? moved[sl] : -1)) : undefined;
  if (oldStatic !== newStatic && oldStatic && newStatic && structSlot) {
    for (let j = 0; j < newStatic.length; j++) {
      const st = newStatic[j], was = oldStatic[j];
      if (st === was) continue;
      if (st.id !== was.id) return null;
      const o = st.planetIndex === planetIndex ? structureObstacle(state, planet, st) : null;
      if ((structSlot[j] >= 0) !== (o !== null)) return null;
      if (o) { list[structSlot[j]] = o; cells[structSlot[j]] = cellOf(o.dir.x, o.dir.y, o.dir.z, n0); }
    }
  }
  // the median radius as the full build's sort reads it, and the cells per edge from it
  const radii = new Float64Array(count);
  let maxRadius = 0, maxHeight = 0;
  for (let i = 0; i < count; i++) { const o = list[i]; radii[i] = o.radius; if (o.radius > maxRadius) maxRadius = o.radius; if (o.height > maxHeight) maxHeight = o.height; }
  if (!allFinite(radii)) return null;
  const median = radii.length ? kthOfSort(radii, radii.length >> 1) : undefined;
  const n = cellsPerEdge(planet.radius, median !== undefined ? median * 2 : planet.radius);
  const cellsN = n === n0 ? cells : cellsOfItems(count, i => list[i].dir, n);
  // the flora's slots and the felled trees' (the kept in flora order, then the joined; `removed` is ascending)
  const floraSlot = new Int32Array(newFlora.length).fill(-1);
  for (let i = 0, j = 0, r = 0; i < oldFlora.length; i++) {
    if (r < sp.removed.length && sp.removed[r] === i) { r++; continue; }
    const sl = oldSlot[i];
    floraSlot[j] = sl >= 0 ? moved[sl] : -1;
    j++;
  }
  for (let t = 0; t < addAt.length; t++) if (addAt[t] >= 0) floraSlot[sp.addStart + t] = at + addAt[t];
  const felled: FelledSlot[] = [];
  for (const x of filed.felled!) if (!left.has(x.f)) felled.push(x.i >= 0 ? { ...x, i: moved[x.i] } : x);
  for (const { x, k } of addFelled) { if (k >= 0) x.i = at + k; felled.push(x); }
  const value: PlanetObstacles = { list, index: fileCellIndex(cellsN, n), maxRadius, maxHeight };
  simWork.tableJoinPatches++;
  memoSet(planet, fk, { from, planetIndex, value });
  lastFiled.set(fk, { radius: planet.radius, value, from, felled, floraSlot, median, planet, cells: cellsN, floraEnd: at + add.length, ...(structSlot ? { structSlot } : {}) });
  splices.set(value, { prev: filed.value, gone, at, count: add.length });
  return value;
}

/** How many of the first `end` flags are set. */
function countBelow(flags: Uint8Array, end: number): number {
  let c = 0;
  for (let i = 0; i < end; i++) c += flags[i];
  return c;
}

/**
 * A static structure's walk obstacle (null: none), kept while it, the config and the planet's radius are the same objects
 * (62.56) — the full build's and 63.1f's follow-up patch's one way to make it.
 */
function structureObstacle(state: Pick<GameState, 'structures' | 'planets'> & Partial<Pick<GameState, 'config'>>, planet: PlanetState, s: StructureState): WalkObstacle | null {
  const c = entityObstacles.get(s);
  if (c && c.config === state.config && c.radius === planet.radius) return c.o;
  const stepOver = state.config ? personSetOf({ config: state.config }).stepOver : ACTION_STEP_OVER;
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  const make = (dir: Vec3, radius: number, height: number, ref: ObstacleRef): WalkObstacle | null => (height >= stepOver ? { dir: vecNormalize(dir), radius, height, ref } : null);
  const build = (): WalkObstacle | null => {
  // 63.3 / 63.4 step 1: a destroyed structure blocks as its rubble stands (the table's measured share), not at full size
  const stands = s.health > 0 ? 1 : structureRuinHeight(s, state.config);   // 63.7 (b): a building sized by function, its own ruin's
  // I1: an information tower is person-scale (two people tall, a person wide), not a planet-scale building
  // 64.11: on the action clock an emplacement is person-scale (lane E's measured body), cover to the shield's height
  const half = personScaleStructureHalfWidth(s, state.config);
  if (half !== null && state.config) {
    const ps = personSetOf({ config: state.config });
    const cover = structureJob(s).cover!;
    // a tower's body stands below the eye at the top (the watcher looks over it, never through it): its cover height
    // is a share of its eye; a gun's or a crate's is a share of a person
    return make(s.surfaceDir, half, (cover.ruler === 'unit' ? ps.towerEyeHeight * cover.height : cover.height * (PERSON_BODY_HEIGHT * ps.k)) * stands, { kind: 'structure', id: s.id });
  }
  // 63.7 (b): a planet-scale building sized by function stands as its own model's shape (in people), on every planet
  if (state.config && isPlanetBuilding(s.type) && buildingsByFunction(state.config)) {
    const { half: h, height } = hitboxExtent(structureHitbox(s, state.config));
    return make(s.surfaceDir, h * person, height * person * stands, { kind: 'structure', id: s.id });
  }
  const k = surfacePropScale(planet.radius);
  return make(s.surfaceDir, STRUCTURE_MODEL_SIZE.half * k, STRUCTURE_MODEL_SIZE.height * k * stands, { kind: 'structure', id: s.id });
  };
  const o = build();
  entityObstacles.set(s, { config: state.config, radius: planet.radius, o });
  return o;
}

/**
 * The obstacles whose centre lies within `angle` radians of direction `d` — a superset, in list order, so an
 * exact test over them answers as it would over the whole list (`ordered` false: any order — for a test that
 * only asks whether any obstacle is hit). Small lists come back whole.
 */
export function obstaclesNear(po: PlanetObstacles, d: Vec3, angle: number, ordered = true): readonly WalkObstacle[] {
  if (po.list.length < 32 || !(angle < Math.PI / 4)) return po.list;
  // (62.57: the ids from the cells' buffer — only the list returned is new; ascending when ordered, as queryCellIndex gives them)
  const count = queryCellIndexCount(po.index, d, angle, ordered), ids = cellQueryIds();
  if (count === po.list.length) return po.list;
  const out: WalkObstacle[] = new Array(count);
  for (let i = 0; i < count; i++) out[i] = po.list[ids[i]];
  return out;
}

/**
 * 62.57: `obstaclesNear` (ordered) into the first entries of a caller's buffer, their count returned — the same obstacles
 * in the same order, nothing allocated (the buffer is never shortened: an array emptied gives its store back).
 */
export function obstaclesNearInto(po: PlanetObstacles, d: Vec3, angle: number, out: WalkObstacle[]): number {
  if (po.list.length < 32 || !(angle < Math.PI / 4)) { for (let i = 0; i < po.list.length; i++) out[i] = po.list[i]; return po.list.length; }
  const count = queryCellIndexCount(po.index, d, angle, true), ids = cellQueryIds();
  for (let i = 0; i < count; i++) out[i] = po.list[ids[i]];
  return count;
}

/** The ruins, where they are bodies (63.7 b: where the buildings are sized by function); else none. */
function ruinBodies(state: Partial<Pick<GameState, 'config' | 'ruins'>>): GameState['ruins'] | undefined {
  return state.config && state.ruins?.length && buildingsByFunction(state.config) ? state.ruins : undefined;
}

/**
 * 62.56: an entity's walk obstacle, kept while the entity, the config and the planet's radius are the same objects —
 * nothing else in it reads the world (a felled tree's lying trunk reads the ground, and is never kept). So a crater,
 * which makes a new planet but leaves the props alone, refiles the same obstacle objects, and core/hits.ts keeps their
 * shapes where the ground under them did not move. Null: the entity stands no obstacle.
 */
let entityObstacles = new WeakMap<object, { config: unknown; radius: number; o: WalkObstacle | null }>();
/** 62.56: a felled tree's obstacle, kept while its foot on the ground and the blow's `from` are the same values too. */
/** Partial destruction step 3: a tree past whole, by its form (its own object stays the same while its health falls). */
let formedObstacles = new WeakMap<object, { config: unknown; radius: number; form: number; o: WalkObstacle | null }>();
let felledObstacles = new WeakMap<object, { config: unknown; radius: number; foot: Vec3; from: Vec3 | undefined; o: WalkObstacle | null; rest?: { planet: PlanetState; at: LyingRest } }>();
type FelledWood = TreeWoodVariant & { stump: readonly WoodPiece[]; fallen: readonly WoodPiece[] };
/** A felled tree as last filed: its slot in the list (-1: it stands no obstacle) and what its obstacle is made from. */
type FelledSlot = { f: FloraState; i: number; up: Vec3; k: number; wood: FelledWood; from: Vec3 | undefined; stepOver: number };
/**
 * A felled kit tree's obstacle (63.4 step 8's forms): its stump where it stood and its trunk lying away from the blow
 * that felled it — kept while the config, the planet's radius, its stump's ground and the blow's `from` are the same.
 * Only its pieces read the ground (the stump's foot: the fall's way); whether it stands one, its direction and its size do not.
 */
type FloraCtx = { state: Pick<GameState, 'flora' | 'planets'> & Partial<Pick<GameState, 'config' | 'propEvents' | 'floraHealth'>>; planetIndex: number; planet: PlanetState; person: number; prop: number; stepOver: number };
/**
 * One tree's walk obstacle on the planet, kept per entity as before (null: it stands none) — with its felled slot when it
 * lies felled; undefined when it is not this planet's or is no kit tree with a trunk (a felled one without stump wood).
 */
function floraObstacle(x: FloraCtx, f: FloraState): { o: WalkObstacle | null; felled?: FelledSlot } | undefined {
  const { state, planetIndex, planet, person, prop, stepOver } = x;
  const size = TREE_TRUNK_SIZE[f.floraType];
  if (!size || f.planetIndex !== planetIndex) return undefined;
  const k = f.scale * (f.size === 'normal' ? NORMAL_TREE_PERSONS * person : TREE_DRAW_SCALE * prop);
  // E5.2: a normal tree blocks shots and sight as it is drawn — its drawn variant's measured wood
  const wood = f.size === 'normal' ? TREE_WOOD[f.floraType]?.variants[treeVariant(f.id, f.floraType)] : undefined;
  const up = vecNormalize(f.surfaceDir);
  if (!f.alive) {
    // a felled kit tree (63.4 step 8's forms): its stump where it stood (walking meets it too) and its trunk lying away
    // from the blow that felled it (the prop log's `from`; a hashed way when nothing says — as the stands draw it)
    if (!wood?.stump || !wood.fallen) return undefined;
    let from: Vec3 | undefined;
    for (const e of state.propEvents ?? []) if (e.felled && e.kind === 'flora' && e.id === f.id && e.from) from = e.from;
    // (403: as the tree was drawn — its own stump and trunk)
    const felled: FelledSlot = { f, i: -1, up, k, wood: drawnWoodVariant(f, wood) as FelledWood, from, stepOver };
    return { o: felledObstacle(planet, state.config, felled), felled };
  }
  // 74.4 (DECISIONS 351): a fallen log, a dressing piece — its wood lying along the ground, as a felled tree's trunk:
  // walking meets it as a row of discs (core/hits.ts lyingDiscs) and its own disc at its middle, shots and sight its wood
  if (wood && f.floraType === 'log') {
    const c = entityObstacles.get(f);
    if (c && c.config === state.config && c.radius === planet.radius) return { o: c.o };
    const o: WalkObstacle | null = size.height * k >= stepOver ? { dir: up, radius: size.half * k, height: size.height * k, ref: { kind: 'flora', id: f.id }, seat: { dir: f.surfaceDir, half: 0 }, pieces: treePieces(wood.whole, up, treeYaw(f.id), k, wood.height), lyingFrom: 0 } : null;
    entityObstacles.set(f, { config: state.config, radius: planet.radius, o });
    return { o };
  }
  // partial destruction step 3: a tree past whole stands as its form's wood (its trunk's foot as before)
  const form = treeForm(state, f), formWood = treeFormShape(f, form)?.wood;
  if (formWood && wood) {
    const c = formedObstacles.get(f);
    if (c && c.config === state.config && c.radius === planet.radius && c.form === form) return { o: c.o };
    const tall = size.height * k * drawnHeightScale(f, wood);
    const o: WalkObstacle | null = tall >= stepOver ? { dir: up, radius: size.half * k, height: tall, ref: { kind: 'flora', id: f.id }, seat: { dir: f.surfaceDir, half: 0 }, pieces: treePieces(drawnPieces(f, wood, formWood), up, treeYaw(f.id), k, wood.height) } : null;
    formedObstacles.set(f, { config: state.config, radius: planet.radius, form, o });
    return { o };
  }
  const c = entityObstacles.get(f);
  if (c && c.config === state.config && c.radius === planet.radius) return { o: c.o };
  // 63.17: walking meets the wood within a person's height of the ground (the foot), at either size; a giant's shots its
  // upright trunk (the drawn variant's measured wood, the same models at the planet's scale)
  const giant = wood ? undefined : TREE_WOOD[f.floraType]?.variants[treeVariant(f.id, f.floraType)];
  // (403: a family tree as it is drawn — its own trunk, lean and crown — its foot's disc and its height too)
  const drawn = wood && drawnWoodVariant(f, wood), tall = size.height * k * (wood ? drawnHeightScale(f, wood) : 1);
  const v = drawn ?? giant, radius = v ? woodFootRadius(v, person / k) * k : size.half * k;
  const pieces = drawn ? treePieces(drawn.whole, up, treeYaw(f.id), k, drawn.height) : giant ? treePieces(giantTrunk(giant, size.height), up, 0, k, giant.height) : undefined;
  const o: WalkObstacle | null = tall >= stepOver ? { dir: up, radius, height: tall, ref: { kind: 'flora', id: f.id }, seat: { dir: f.surfaceDir, half: 0 }, ...(pieces?.length ? { pieces } : {}) } : null;
  entityObstacles.set(f, { config: state.config, radius: planet.radius, o });
  return { o };
}
/**
 * 74.4 (DECISIONS 351): a long low rock (constants LYING_ROCKS: the ledge) as a disc of its length would keep walkers
 * three people off a face a body should lie against; it lies instead as one frustum along its drawn hull (constants
 * NORMAL_ROCK_HULL, its drawn variant turned by its drawn yaw), as a felled trunk lies: the hull's depth its radius, its
 * top the drawn rock's top over the ground.
 */
/** A lying rock's piece (world units, from its foot) and its own disc (the hull's half depth, at its middle); null without a hull. */
export function lyingRock(r: Pick<RockState, 'id' | 'rockType' | 'surfaceDir'>, n: { height: number }, k: number): { radius: number; pieces: ObstaclePiece[] } | null {
  const hull = NORMAL_ROCK_HULL[r.rockType]?.[rockVariant(r.id)];
  if (!hull) return null;
  const frame = treeFrame(vecNormalize(r.surfaceDir), rockYaw(r.id)), ct = dcos(hull.turn), st = dsin(hull.turn);
  const y = n.height * (1 - ROCK_SINK_FRACTION) - hull.hz;   // (the axis: its top the drawn top over the ground)
  const end = (sgn: number) => frame((hull.cx + sgn * ct * hull.hx) * k, y * k, (hull.cz + sgn * st * hull.hx) * k);
  return { radius: hull.hz * k, pieces: [{ a: end(-1), b: end(1), ra: hull.hz * k, rb: hull.hz * k }] };
}
function felledObstacle(planet: PlanetState, config: unknown, x: FelledSlot): WalkObstacle | null {
  const { f, up, k, wood, from, stepOver } = x;
  // (71.2: its foot where the standing tree's was — on the fine ground at its trunk — and so its fall's way from it)
  const ps = config ? personSetOf({ config: config as GameConfig }) : null;
  const foot = vecAdd(planet.position, vecScale(up, seatRadius(planet, { dir: f.surfaceDir, half: 0 }, up, ps)));
  const c = felledObstacles.get(f);
  const yaw = treeFallYaw(f.id, up, foot, from);
  let rest: LyingRest | null | undefined;
  if (c && c.config === config && c.radius === planet.radius && sameVec(c.foot, foot) && sameVec(c.from, from)) {
    // (70.4: a trunk resting on the ground along its length — on another planet object (an edit somewhere) its rest is found
    // again, and while it is the same the obstacle is kept, the same object)
    if (!c.rest || c.rest.planet === planet) return c.o;
    rest = fallenRest(planet, foot, up, yaw, k, wood.fallen, ps);
    if (rest && rest.y0 === c.rest.at.y0 && rest.cos === c.rest.at.cos && rest.sin === c.rest.at.sin) { c.rest.planet = planet; return c.o; }
  }
  const stumpTop = wood.stump.reduce((m, p) => Math.max(m, p[1], p[5]), 0) * k;
  if (rest === undefined) rest = fallenRest(planet, foot, up, yaw, k, wood.fallen, ps);
  const pieces = [...treePieces(wood.stump, up, treeYaw(f.id), k, wood.height), ...(rest ? restedPieces(wood.fallen, up, yaw, k, rest) : treePieces(wood.fallen, up, yaw, k, wood.height))];
  const o: WalkObstacle | null = stumpTop >= stepOver ? { dir: up, radius: wood.stump[0][3] * k, height: stumpTop, ref: { kind: 'flora', id: f.id }, seat: { dir: f.surfaceDir, half: 0 }, pieces, lyingFrom: wood.stump.length, ...(rest ? { rested: true as const } : {}) } : null;
  felledObstacles.set(f, { config, radius: planet.radius, foot, from, o, ...(rest ? { rest: { planet, at: rest } } : {}) });
  return o;
}
const sameVec = (a: Vec3 | undefined, b: Vec3 | undefined) => a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.z === b.z);

function buildWalkObstacles(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets'> & Partial<Pick<GameState, 'config' | 'propEvents' | 'ruins' | 'floraHealth' | 'debris'>>, planetIndex: number, felledOut?: FelledSlot[], floraSlot?: Int32Array, structSlot?: Int32Array, ends?: { flora: number }): WalkObstacle[] {
  const out: WalkObstacle[] = [];
  const planet = state.planets[planetIndex];
  if (!planet) return out;
  const prop = surfacePropScale(planet.radius) / 2;
  // a person steps over what is lower than their step (scale profile: a person number)
  const stepOver = state.config ? personSetOf({ config: state.config }).stepOver : ACTION_STEP_OVER;
  const make = (dir: Vec3, radius: number, height: number, ref: ObstacleRef): WalkObstacle | null => (height >= stepOver ? { dir: vecNormalize(dir), radius, height, ref } : null);
  const kept = (entity: object, build: () => WalkObstacle | null): void => {
    const c = entityObstacles.get(entity);
    let o: WalkObstacle | null;
    if (c && c.config === state.config && c.radius === planet.radius) o = c.o;
    else { o = build(); entityObstacles.set(entity, { config: state.config, radius: planet.radius, o }); }
    if (o) out.push(o);
  };
  // E5: normal-size biome props are sized from the person (constants NORMAL_*_PERSONS), giant ones from the planet
  const person = PERSON_BODY_HEIGHT * (state.config ? personSetOf({ config: state.config }).k : 1);
  for (const r of state.rocks ?? []) {
    if (!r.alive || r.planetIndex !== planetIndex) continue;
    kept(r, () => {
      if (r.size === 'normal') {
        const n = NORMAL_ROCK_SIZE[r.rockType];
        if (!n) return null;
        const h = n.height * r.scale * NORMAL_ROCK_PERSONS * person;
        const radius = n.half * r.scale * NORMAL_ROCK_PERSONS * person;   // (its seat's footprint too: core/propSeat.ts rockSeatHalf)
        // 74.4: a long low rock (the ledge) lies along its hull — walking meets a row of discs, shots its length (lyingRock)
        if (LYING_ROCKS.has(r.rockType)) {
          const lying = lyingRock(r, n, r.scale * NORMAL_ROCK_PERSONS * person);
          return lying && h >= stepOver ? { dir: vecNormalize(r.surfaceDir), radius: lying.radius, height: h, ref: { kind: 'rock', id: r.id }, seat: { dir: r.surfaceDir, half: radius }, pieces: lying.pieces, lyingFrom: 0 } : null;
        }
        return h >= stepOver ? { dir: vecNormalize(r.surfaceDir), radius, height: h, ...(NORMAL_ROCK_PROFILE[r.rockType] ? { profile: NORMAL_ROCK_PROFILE[r.rockType], sink: ROCK_SINK_FRACTION } : {}), ref: { kind: 'rock', id: r.id }, seat: { dir: r.surfaceDir, half: radius } } : null;
      }
      const size = ROCK_MODEL_SIZE[r.mode] ?? ROCK_MODEL_SIZE.medium;
      const k = r.scale * (ROCK_MODE_SCALES[r.mode] ?? 3) * prop;
      const o = make(r.surfaceDir, size.half * k, size.height * k, { kind: 'rock', id: r.id });
      return o && { ...o, seat: { dir: r.surfaceDir, half: 0 } };   // (a planet-scale rock: seated at its centre)
    });
  }
  const ctx: FloraCtx = { state, planetIndex, planet, person, prop, stepOver };
  const flora = state.flora ?? [];
  for (let j = 0; j < flora.length; j++) {
    const r = floraObstacle(ctx, flora[j]);
    if (!r) continue;
    if (r.felled) felledOut?.push(r.felled);
    if (!r.o) continue;
    if (r.felled) r.felled.i = out.length;
    if (floraSlot) floraSlot[j] = out.length;
    out.push(r.o);
  }
  if (ends) ends.flora = out.length;   // (E5.4's tail patch: where the flora's obstacles end)
  // (63.1f's follow-up: in staticStructures' order — the order the patch above walks — each one's slot recorded)
  const statics = staticStructures(state.structures) ?? [];
  for (let j = 0; j < statics.length; j++) {
    const s = statics[j];
    if (s.planetIndex !== planetIndex) continue;
    const o = structureObstacle(state, planet, s);
    if (!o) continue;
    if (structSlot) structSlot[j] = out.length;
    out.push(o);
  }
  // 63.7 (b)'s follow-up: where the buildings are sized by function a ruin is a body too, its person-scale model as it stands
  for (const r of ruinBodies(state) ?? []) {
    if (r.planetIndex !== planetIndex) continue;
    kept(r, () => {
      const { half: h, height } = hitboxExtent(ruinHitbox(r));
      return make(r.surfaceDir, h * person, height * person, { kind: 'ruin', id: r.id });
    });
  }
  // partial destruction step 3: what fell — a lying limb (walking meets it as discs where it stands above the step, as a
  // felled trunk; shots and sight meet its wood, low cover) or a heap (a mound)
  if (state.config?.partialDestruction) for (const d of state.debris ?? []) {
    if (d.planetIndex !== planetIndex) continue;
    kept(d, () => {
      const up = vecNormalize(d.dir), { radius, height } = debrisExtent(d), rest = debrisRest(planet, d, personSetOf({ config: state.config! }));
      return { dir: up, radius, height, ref: { kind: 'debris', id: d.id }, seat: { dir: d.dir, half: 0 }, pieces: debrisPieces(d, up, rest), ...(d.heap === undefined ? { lyingFrom: 0 } : {}), ...(rest ? { rested: true as const } : {}) };
    });
  }
  return out;
}

/**
 * 63.16 (i): how far a body's centre at unit direction `n` stands off obstacle `o` along the ground (world; negative inside),
 * and the ground direction from the body toward the obstacle's nearest point (null when it is at the obstacle's centre):
 * a disc's rim, or the box's rectangle — the body's offset in the box's frame, clamped to the box, is the nearest point;
 * inside the box the nearest edge, and `toward` its outward normal reversed (the push out).
 */
export function obstacleGap(o: WalkObstacle, n: Vec3, R: number): { gap: number; toward: Vec3 | null } {
  const b = o.box;
  if (!b) {
    const along = dacos(Math.max(-1, Math.min(1, vecDot(n, o.dir)))) * R;
    const t = vecSub(o.dir, n), tl = vecLength(projectRaw(t, n));
    return { gap: along - o.radius, toward: tl > 1e-12 ? projectToTangent(t, n) : null };
  }
  const off = vecScale(vecSub(n, b.c), R);
  const x = vecDot(off, b.ax), z = vecDot(off, b.az);
  const dx = Math.abs(x) - b.hx, dz = Math.abs(z) - b.hz;
  if (dx > 0 || dz > 0) {
    const ex = Math.max(0, dx), ez = Math.max(0, dz);
    const gap = Math.sqrt(ex * ex + ez * ez);
    // (toward the nearest point: back along each axis the body stands past)
    const tx = ex > 0 ? -Math.sign(x) * ex : 0, tz = ez > 0 ? -Math.sign(z) * ez : 0;
    const t = vecAdd(vecScale(b.ax, tx), vecScale(b.az, tz));
    return { gap, toward: vecLength(t) > 1e-12 ? projectToTangent(t, n) : null };
  }
  // inside: out by the nearest edge — `toward` into the box, away from that edge (the step that goes deeper is the blocked one)
  const gap = Math.max(dx, dz);   // (≤ 0: how deep in, as a negative gap)
  const t = dx >= dz ? vecScale(b.ax, -Math.sign(x || 1)) : vecScale(b.az, -Math.sign(z || 1));
  return { gap, toward: projectToTangent(t, n) };
}
/** `v` with its component along `n` removed (not normalised). */
function projectRaw(v: Vec3, n: Vec3): Vec3 { return vecSub(v, vecScale(n, vecDot(v, n))); }

/** A unit's heading, or a default one in its ground plane. */
export function headingOf(p: Pick<PlayerState, 'heading' | 'surfaceDir'>): Vec3 {
  const n = vecNormalize(p.surfaceDir);
  if (p.heading) return projectToTangent(p.heading, n);
  return surfaceTangentFrame(n).tx;
}

/** `v` with its component along `n` removed, normalised (a ground-plane direction). */
export function projectToTangent(v: Vec3, n: Vec3): Vec3 {
  const t = vecSub(v, vecScale(n, vecDot(v, n)));
  const len = vecLength(t);
  return len > 1e-12 ? vecScale(t, 1 / len) : surfaceTangentFrame(n).tx;
}

/** Across the heading, to the unit's right (heading × up). */
export function rightOf(heading: Vec3, n: Vec3): Vec3 {
  return vecNormalize(vecCross(heading, n));
}

/** Turn a heading about the up axis: + turns toward the right. */
export function turnHeading(heading: Vec3, n: Vec3, angle: number): Vec3 {
  if (angle === 0) return heading;
  const right = rightOf(heading, n);
  return projectToTangent(vecAdd(vecScale(heading, dcos(angle)), vecScale(right, dsin(angle))), n);
}

/** Rotate `v` about the unit axis `k` by `angle` (Rodrigues). */
function rotate(v: Vec3, k: Vec3, angle: number): Vec3 {
  const c = dcos(angle), s = dsin(angle);
  const kv = vecDot(k, v);
  return vecAdd(vecAdd(vecScale(v, c), vecScale(vecCross(k, v), s)), vecScale(k, kv * (1 - c)));
}

export interface WalkStep {
  surfaceDir: Vec3;
  heading: Vec3;
  /** Units actually walked this step (0 when blocked or not moving). */
  moved: number;
  blocked: boolean;
  /** Decision 134: the step was a scramble (up ground too steep to walk, at `body.scramble` of the pace along it). */
  scrambled?: boolean;
}

/**
 * One step of walking: `forward` / `right` in −1…1 in the unit's own frame, `speed` in
 * units per second, for `dt` seconds. Blocked by obstacles (sliding along them when the
 * step only grazes one) and by ground too steep to climb.
 */
/** 62.57: stepWalk's candidates (in cell order) copied from the cells' buffer into its own, which only grows — a view of the first `count`. */
let walkIds = new Uint32Array(256);
function walkCandidates(index: CellIndex, n: Vec3, angle: number): Uint32Array {
  const count = queryCellIndexCount(index, n, angle, false), ids = cellQueryIds();
  if (count > walkIds.length) walkIds = new Uint32Array(Math.max(count, 2 * walkIds.length));
  walkIds.set(ids.subarray(0, count));
  return walkIds.subarray(0, count);
}
export function stepWalk(
  surfaceDir: Vec3, heading: Vec3, planet: PlanetState, obstacles: readonly WalkObstacle[],
  forward: number, right: number, speed: number, dt: number,
  body: { unitRadius: number; maxSlope: number; scramble?: number; fineAmplitude?: number; fineWavelength?: number; k?: number; materialRelief?: boolean; uphill?: number } = { unitRadius: ACTION_UNIT_RADIUS, maxSlope: ACTION_MAX_SLOPE },
  /** 62.10: the planet's filed obstacles (`obstacles` must be its list) — only those near this step are tested. */
  filed?: PlanetObstacles,
): WalkStep {
  const n = vecNormalize(surfaceDir);
  const h = projectToTangent(heading, n);
  const r = rightOf(h, n);
  let move = vecAdd(vecScale(h, forward), vecScale(r, right));
  const amount = Math.min(1, vecLength(move));
  if (amount < 1e-9) return { surfaceDir: n, heading: h, moved: 0, blocked: false };
  move = vecNormalize(move);
  const dist = speed * dt * amount;
  // S3: at person scale the ground underfoot is the fine ground (the mesh plus its hummocks); the mesh alone at classic
  // (71 step 0: with the person's scale too, so the walk stands on the digs and craterlets as eyes and bullets do — it left `k` out)
  const fine = body.fineAmplitude !== undefined && body.fineAmplitude > 0 && body.fineWavelength !== undefined && body.k !== undefined
    ? { fineAmplitude: body.fineAmplitude, fineWavelength: body.fineWavelength, k: body.k, materialRelief: body.materialRelief === true } : null;
  const groundAt = (d: Vec3): number => fine ? personSurfaceRadius(planet, d, fine) : getSurfaceRadius(planet, d);
  const radius = groundAt(n);
  // Only obstacles a step can reach: within (their radius + ours) of where it lands, so of the start + the step.
  // Candidates come in cell order; the step is blocked by the earliest-listed one in the way, as over the whole list
  const ids = filed && filed.list === obstacles && obstacles.length >= 32
    ? walkCandidates(filed.index, n, ((filed.maxRadius * (filed.boxed ? Math.SQRT2 : 1) + body.unitRadius + dist) / radius) * (1 + 1e-9) + 1e-12)   // (62.57: the step's own buffer; 63.16 (i): a box reaches past its disc)
    : null;

  for (let attempt = 0; attempt < 2; attempt++) {
    const theta = dist / radius;
    const next = vecNormalize(vecAdd(vecScale(n, dcos(theta)), vecScale(move, dsin(theta))));
    // Feet: the first obstacle in the way (the earliest in the list), if any
    let hit: WalkObstacle | null = null, hitToward: Vec3 | null = null;
    let hitAt = Infinity;
    const count = ids ? ids.length : obstacles.length;
    for (let c = 0; c < count; c++) {
      const idx = ids ? ids[c] : c;
      if (idx >= hitAt) continue;
      const o = obstacles[idx];
      const dn = vecDot(next, o.dir);
      if (!mayBeWithin(dn, ((o.box ? o.radius * Math.SQRT2 : o.radius) + body.unitRadius) / radius)) continue;
      if (o.box) {
        // 63.16 (i): the rectangle itself — blocked when the step ends within the body's radius of it and nearer than it began;
        // the slide is along it as seen from where the body stands (at a corner, the way from the step's end turns the slide in)
        const at = obstacleGap(o, next, radius);
        if (at.gap < body.unitRadius) { const now = obstacleGap(o, n, radius); if (at.gap < now.gap) { hit = o; hitToward = now.toward ?? at.toward; hitAt = idx; if (!ids) break; } }
        continue;
      }
      const along = dacos(Math.max(-1, Math.min(1, dn))) * radius;
      if (along < o.radius + body.unitRadius) {
        // Only block moving into it (a unit already touching one may walk away)
        const before = dacos(Math.max(-1, Math.min(1, vecDot(n, o.dir)))) * radius;
        if (along < before) { hit = o; hitToward = null; hitAt = idx; if (!ids) break; }
      }
    }
    if (hit) {
      // Slide: drop the part of the step that heads into the obstacle, try once more (63.16 (i): toward a box's nearest point)
      const toward = hitToward ?? projectToTangent(vecSub(hit.dir, n), n);
      const into = vecDot(move, toward);
      const slid = vecSub(move, vecScale(toward, Math.max(0, into)));
      if (attempt === 1 || vecLength(slid) < 0.2) return { surfaceDir: n, heading: h, moved: 0, blocked: true };
      move = vecNormalize(slid);
      continue;
    }
    // Ground: too steep a rise stops the step (going down is always allowed) — unless the body scrambles (decision 134:
    // no ground is a trap): then it goes up it at `scramble` of the pace along the slope, so the steeper the slower across
    const rise = groundAt(next) - radius;
    let walked = dist, to = next, turn = theta, scrambled = false;
    if (rise > dist * body.maxSlope) {
      if (!body.scramble || body.scramble <= 0) return { surfaceDir: n, heading: h, moved: 0, blocked: true };
      const grade = rise / dist;
      walked = (dist * body.scramble) / Math.sqrt(1 + grade * grade);
      turn = walked / radius;
      to = vecNormalize(vecAdd(vecScale(n, dcos(turn)), vecScale(move, dsin(turn))));
      scrambled = true;
    } else if (body.uphill !== undefined && body.uphill > 0 && rise > 0) {
      // 63.5 step 4: ground that gives way underfoot (sand, ash) costs pace going up, by the grade
      walked = dist * Math.max(0.2, 1 - body.uphill * (rise / dist));
      turn = walked / radius;
      to = vecNormalize(vecAdd(vecScale(n, dcos(turn)), vecScale(move, dsin(turn))));
    }
    // Carry the heading along the same rotation (parallel transport)
    const axis = vecCross(n, move);
    const axisLen = vecLength(axis);
    const carried = axisLen > 1e-12 ? rotate(h, vecScale(axis, 1 / axisLen), turn) : h;
    return { surfaceDir: to, heading: projectToTangent(carried, to), moved: walked, blocked: false, ...(scrambled ? { scrambled: true } : {}) };
  }
  return { surfaceDir: n, heading: h, moved: 0, blocked: true };
}

/**
 * 63.16 (1), (2) (the owner, Oct 20: "you can jump through the wall of a building, run into wall and jump and you'll get
 * through", "you can jump through hq hesco barrier"): an airborne person against the walk's obstacles. The walk's test
 * above runs for a grounded unit only, and a jump (or a knockback) flew through any wall with the walk's carry. One
 * rule, the walk's own: the flight's move this tick is blocked by an obstacle whose top stands above the feet — the
 * position is set back to the obstacle's rim (never further out than it was) and the velocity into it dropped, a slide
 * along it as the walk's — so a jump at a wall ends at its foot and a hop over something lower than the feet clears it.
 * `from` is where the flight was before this tick's step, `pos` and `vel` after it; `groundAt` the ground's radius at a
 * direction (the fine ground for a person); `filed` the planet's obstacles for the mover (core/hits.ts
 * footprintObstacles). Null: nothing in the way. Deterministic (dmath); its cost is the walk's query, for the airborne only.
 */
export function airborneAgainstObstacles(
  from: Vec3, pos: Vec3, vel: Vec3, planet: PlanetState, filed: PlanetObstacles, unitRadius: number, groundAt: (d: Vec3) => number,
): { pos: Vec3; vel: Vec3 } | null {
  if (filed.list.length === 0) return null;
  const nFrom = vecNormalize(vecSub(from, planet.position));
  let p = pos, v = vel, moved = false;
  for (let attempt = 0; attempt < 2; attempt++) {
    const rel = vecSub(p, planet.position), r = vecLength(rel);
    if (r < 1e-9) return moved ? { pos: p, vel: v } : null;
    const n = vecScale(rel, 1 / r), ground = groundAt(n), feet = r - ground;
    if (feet >= filed.maxHeight) break;   // (above every top: nothing can be in the way)
    const step = vecLength(vecSub(p, from));
    const ids = filed.list.length >= 32 ? walkCandidates(filed.index, n, ((filed.maxRadius * (filed.boxed ? Math.SQRT2 : 1) + unitRadius + step) / ground) * (1 + 1e-9) + 1e-12) : null;
    const count = ids ? ids.length : filed.list.length;
    let hit: WalkObstacle | null = null, hitAt = Infinity, hitAlong = 0, hitBefore = 0, hitToward: Vec3 | null = null;
    for (let c = 0; c < count; c++) {
      const idx = ids ? ids[c] : c;
      if (idx >= hitAt) continue;
      const o = filed.list[idx];
      if (feet >= o.height) continue;   // (over its top)
      const dn = vecDot(n, o.dir);
      if (!mayBeWithin(dn, ((o.box ? o.radius * Math.SQRT2 : o.radius) + unitRadius) / ground)) continue;
      // (63.16 (i): a box's rectangle, else the disc's rim — the gap to it now and where the flight was before this tick)
      const now = obstacleGap(o, n, ground), was = obstacleGap(o, nFrom, ground);
      if (now.gap < unitRadius && now.gap < was.gap && now.toward) {
        // Only block moving into it (a unit that took off touching one may fly away)
        hit = o; hitAt = idx; hitAlong = now.gap; hitBefore = was.gap; hitToward = now.toward; if (!ids) break;
      }
    }
    if (!hit) break;
    // Back to its rim along the ground (never further out than it was), the velocity into it dropped
    const toward = hitToward!;
    const out = Math.min(unitRadius, hitBefore) - hitAlong;
    p = vecSub(p, vecScale(toward, out));
    const into = vecDot(v, toward);
    if (into > 0) v = vecSub(v, vecScale(toward, into));
    moved = true;
  }
  return moved ? { pos: p, vel: v } : null;
}
